import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Lightbulb, Loader2, Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface AIReviewSuggestions {
  title: string;
  titleSuggestion?: string;
  description: string;
  descriptionSuggestion?: string;
  suggestedDomainIds: string[];
  domainConfidence: Record<string, number>;
  overallConfidence: number;
  domainReasonings?: Array<{ id: string; reasoning: string }>;
}

export default function SolutionReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { aiSuggestions, uploadedFiles, sourceMethod } = location.state || {};

  const [title, setTitle] = useState(aiSuggestions?.title || "");
  const [description, setDescription] = useState(aiSuggestions?.description || "");
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(aiSuggestions?.suggestedDomainIds || []);
  const [agency, setAgency] = useState("GSA");
  const [category, setCategory] = useState("");
  const [commercialType, setCommercialType] = useState<"Sale" | "Lease" | "Collaborate" | "Stake">("Sale");
  const [basePrice, setBasePrice] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: domains } = useQuery({
    queryKey: ['domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (!aiSuggestions) {
      toast({
        title: "Missing data",
        description: "Please start from the solution creation page",
        variant: "destructive"
      });
      navigate("/solution-creation");
    }
  }, [aiSuggestions, navigate, toast]);

  const handleUseTitleSuggestion = () => {
    if (aiSuggestions?.titleSuggestion) {
      setTitle(aiSuggestions.titleSuggestion);
      toast({
        title: "Title updated",
        description: "Using Giblet's suggested title"
      });
    }
  };

  const handleUseDescriptionSuggestion = () => {
    if (aiSuggestions?.descriptionSuggestion) {
      setDescription(aiSuggestions.descriptionSuggestion);
      toast({
        title: "Description updated",
        description: "Using Giblet's suggested description"
      });
    }
  };

  const toggleDomain = (domainId: string) => {
    setSelectedDomainIds(prev =>
      prev.includes(domainId)
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please provide a title for your solution",
        variant: "destructive"
      });
      return;
    }

    if (!agency || !category) {
      toast({
        title: "Required fields missing",
        description: "Please fill in agency and category",
        variant: "destructive"
      });
      return;
    }

    setIsPublishing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const priceFactors = { Sale: 1.0, Lease: 0.1, Collaborate: 0.2, Stake: 0.5 };
      const calculatedPrice = basePrice * priceFactors[commercialType];

      const { data: solution, error: solutionError } = await supabase
        .from('solutions')
        .insert({
          creator_id: user.id,
          title: title.trim(),
          description: description.trim() || null,
          agency,
          category,
          commercial_type: commercialType,
          base_price: basePrice,
          price: calculatedPrice,
          status: 'published',
          file_url: uploadedFiles?.[0]?.url,
          file_name: uploadedFiles?.[0]?.name,
          file_type: uploadedFiles?.[0]?.type,
          file_size: uploadedFiles?.[0]?.size,
        })
        .select()
        .single();

      if (solutionError) throw solutionError;

      // Auto-populate domains
      if (selectedDomainIds.length > 0) {
        const { error: domainsError } = await supabase.rpc('auto_populate_solution_domains', {
          solution_id: solution.id,
          domain_ids: selectedDomainIds
        });

        if (domainsError) {
          console.error('Error populating domains:', domainsError);
        }
      }

      toast({
        title: "Solution Published! 🎉",
        description: "Your solution is now live"
      });

      navigate('/myspai-profile');
    } catch (error) {
      console.error('Error publishing solution:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to publish solution",
        variant: "destructive"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!aiSuggestions) return null;

  const confidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "text-green-600";
    if (confidence >= 0.6) return "text-yellow-600";
    return "text-orange-600";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Sparkles className="text-primary" />
            Review & Refine Your Solution
          </h1>
          <p className="text-muted-foreground">
            Giblet has analyzed your content. Review and adjust as needed.
          </p>
        </div>

        {aiSuggestions.overallConfidence > 0 && (
          <Card className="p-4 mb-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="text-blue-600" size={20} />
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                Giblet's Confidence: {Math.round(aiSuggestions.overallConfidence * 100)}%
              </span>
            </div>
          </Card>
        )}

        <Card className="p-6 mb-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter solution title"
                className="mb-2"
              />
              {aiSuggestions.titleSuggestion && title !== aiSuggestions.titleSuggestion && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUseTitleSuggestion}
                  className="w-full justify-start text-left"
                >
                  <Lightbulb className="mr-2" size={16} />
                  <span className="flex-1">
                    Giblet suggests: <span className="font-semibold">{aiSuggestions.titleSuggestion}</span>
                  </span>
                </Button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter solution description"
                rows={4}
                className="mb-2"
              />
              {aiSuggestions.descriptionSuggestion && description !== aiSuggestions.descriptionSuggestion && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUseDescriptionSuggestion}
                  className="w-full justify-start text-left"
                >
                  <Lightbulb className="mr-2" size={16} />
                  <span className="flex-1 line-clamp-2">
                    Giblet suggests: <span className="font-semibold">{aiSuggestions.descriptionSuggestion}</span>
                  </span>
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Agency <span className="text-destructive">*</span></label>
                <Select value={agency} onValueChange={setAgency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GSA">GSA</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="NOAA">NOAA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Category <span className="text-destructive">*</span></label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Procurement"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Commercial Type</label>
                <Select value={commercialType} onValueChange={(v: any) => setCommercialType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sale">Sale</SelectItem>
                    <SelectItem value="Lease">Lease</SelectItem>
                    <SelectItem value="Collaborate">Collaborate</SelectItem>
                    <SelectItem value="Stake">Stake</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Base Price ($)</label>
                <Input
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Knowledge Domains</label>
              <p className="text-sm text-muted-foreground mb-3">
                Select relevant domains for your solution.
              </p>
              <TooltipProvider>
                <div className="flex flex-wrap gap-2">
                  {domains?.map(domain => {
                    const isSelected = selectedDomainIds.includes(domain.id);
                    const confidence = aiSuggestions.domainConfidence[domain.id];
                    const reasoning = aiSuggestions.domainReasonings?.find(r => r.id === domain.id)?.reasoning;
                    const isAISuggested = aiSuggestions.suggestedDomainIds.includes(domain.id);

                    return (
                      <Tooltip key={domain.id}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={isSelected ? "default" : "outline"}
                            className="cursor-pointer transition-all hover:scale-105"
                            onClick={() => toggleDomain(domain.id)}
                          >
                            {isSelected ? (
                              <CheckCircle2 className="mr-1" size={14} />
                            ) : (
                              <XCircle className="mr-1" size={14} />
                            )}
                            {domain.name}
                            {isAISuggested && confidence && (
                              <span className={`ml-1 text-xs ${confidenceColor(confidence)}`}>
                                {Math.round(confidence * 100)}%
                              </span>
                            )}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-sm">{domain.description || domain.name}</p>
                          {reasoning && (
                            <p className="text-xs text-muted-foreground mt-1 pt-1 border-t">
                              AI Reasoning: {reasoning}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </Card>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isPublishing}
            className="flex-1"
          >
            Go Back
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isPublishing || !title.trim() || !agency || !category}
            className="flex-1"
          >
            {isPublishing ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} />
                Publishing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2" size={16} />
                Publish Solution
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
